/**
 * Hydrates the book placeholders baked into imported blog article HTML:
 *
 *   <div class="book-card" data-isbn="9780834829541" data-handle="pocket-pema-chodron"></div>
 *   <div class="book-buy" data-isbn="9780834829541" data-handle="pocket-pema-chodron"></div>
 *
 * into a real product card / add-to-cart button, fetched via the Section
 * Rendering API. See sections/book-card-embed.liquid and
 * sections/book-buy-embed.liquid for the server-rendered fragments.
 *
 * data-handle is resolved from the ISBN at import time (the same mapping that
 * built the product redirects), so there's no runtime lookup: one request per
 * placeholder. data-isbn is kept on the element for debugging only and nothing
 * here reads it.
 *
 * A placeholder whose handle is missing or no longer resolves (e.g. the
 * handle changed since import) is removed rather than left as an empty box.
 */
(() => {
  const SELECTOR = '.book-card, .book-buy';

  // book-card-embed/book-buy-embed are rendered globally via
  // sections/overlay-group.json (see sections/book-card-embed.liquid) so
  // they always exist in the DOM, but Shopify only accepts a section_id
  // that's registered somewhere in the theme - a plain, unregistered
  // section filename 404s on the Section Rendering API. Read their real,
  // store-assigned ids off the page rather than hardcoding the filename.
  function embedSectionId(key) {
    const el = document.querySelector(`[id$="__${key}"]`);
    return el ? el.id.replace('shopify-section-', '') : null;
  }

  function hydrateFragment(el, sectionId, wrapperSelector, onRendered) {
    return fetch(`/products/${el.dataset.handle}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        const rendered = new DOMParser().parseFromString(html, 'text/html').querySelector(wrapperSelector);
        if (rendered) {
          if (onRendered) onRendered(rendered);
          el.replaceWith(rendered);
        } else {
          el.remove();
        }
      })
      .catch(() => el.remove());
  }

  // A book-buy placeholder is a single inline CTA, not a full product page -
  // buy-buttons.liquid always adds its own "Buy it now" alongside "Add to
  // cart", which isn't wanted for this narrower use.
  function stripBuyNow(rendered) {
    rendered.querySelector('[id^="ProductBuyNowButton-"]')?.remove();
  }

  function hydrate(el) {
    const handle = el.dataset.handle;
    if (!handle) {
      el.remove();
      return;
    }

    const key = el.classList.contains('book-buy') ? 'book-buy-embed' : 'book-card-embed';
    const sectionId = embedSectionId(key);
    if (!sectionId) {
      el.remove();
      return;
    }

    const selector = key === 'book-buy-embed' ? '.product-form' : '.product-card';
    hydrateFragment(el, sectionId, selector, key === 'book-buy-embed' ? stripBuyNow : null);
  }

  document.querySelectorAll(SELECTOR).forEach(hydrate);
})();
